import * as React from "react"

import { cn } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton"
import { Card } from "@/components/ui/card"

// Wave 3 — Loading state primitives
// Replaces the 9 ad-hoc <Loader2 /> spinners currently reimplemented per
// section (TasksSection:342, RemindersSection:297, HabitsSection:337, etc.)
// with a coherent skeleton-first approach plus a full-screen orb pulse
// for boot/auth transitions.
//
// All animations use motion-safe:animate-pulse so users with
// prefers-reduced-motion get static placeholders.

// ---------- LoadingScreen ----------
// Full-viewport centered pulse. Use for route transitions / auth bootstrap.
// CSS-only orb (no NexoCore import) to keep this component standalone.
export function LoadingScreen({ label = "Carregando..." }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-surface-canvas gap-6">
      <div className="relative w-16 h-16">
        <div className="absolute inset-0 rounded-full bg-brand-cyan/20 motion-safe:animate-pulse" />
        <div className="absolute inset-2 rounded-full bg-brand-cyan/40 motion-safe:animate-pulse [animation-delay:150ms]" />
        <div className="absolute inset-4 rounded-full bg-brand-cyan" />
      </div>
      <p className="text-caption text-foreground-muted tracking-wide uppercase">{label}</p>
    </div>
  )
}

// ---------- ListSkeleton ----------
// Renders `count` rows mimicking a typical list item (icon circle + two
// text lines). Use where a section waits for /api/tasks etc. to resolve.
export function ListSkeleton({ count = 3, className }) {
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} variant="default" className="flex items-center gap-4 p-5">
          <Skeleton className="w-10 h-10 rounded-full shrink-0" />
          <div className="flex-1 flex flex-col gap-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <Skeleton className="w-16 h-6 rounded-md shrink-0" />
        </Card>
      ))}
    </div>
  )
}

// ---------- StatCardSkeleton ----------
// For dashboard summary tiles (big number + label + delta).
export function StatCardSkeleton({ className }) {
  return (
    <Card variant="default" className={cn("flex flex-col gap-3 p-5", className)}>
      <div className="flex items-center justify-between">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="w-8 h-8 rounded-md" />
      </div>
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-3 w-24" />
    </Card>
  )
}

// ---------- InlineSpinner ----------
// Tiny pulsing dot for within-row loading (e.g. "Salvando..." inline).
// Not a spinner — avoids the rotating-loader cliché. Keeps visual consistency
// with the skeleton pulse language.
export function InlineSpinner({ className }) {
  return (
    <span
      className={cn(
        "inline-block w-2 h-2 rounded-full bg-brand-cyan motion-safe:animate-pulse",
        className
      )}
      aria-label="Carregando"
    />
  )
}
